import Fuse from 'fuse.js'
import dayjs from 'dayjs'
export const useEventsStore = defineStore('EventsStore', () => {

  const {$api, $bugsnag} = useNuxtApp();
  const {dayJsTz} = useCurrentTime();

  const bookmarkedSessions = ref<BookmarkedSession[]>([]);
  const bookmarkedSpeakers = ref<number[]>([]);
  const bookmarkedPartners = ref<number[]>([]);
  const bookmarkedGoodies = ref<number[]>([]);
  const bookmarkedActivities = ref<number[]>([]);
  const dataLoaded: Ref<boolean> = ref(false);

  // Both /bookmarks and /workshops/counts require a ticket for this event — checked here
  // (rather than only by the callers) since getBookmarks is also invoked directly from
  // app.vue's SSE 'reload-bookmarks' handler, outside of loadEvent()'s own guard.
  const {hasEventTicket, currentUserId} = useWadAuth();

  const getBookmarks = async (eventId: number) => {
    if(!hasEventTicket(eventId)) {
      return;
    }
    const { sessions, speakers, partners, goodies, activities } = await $api.v2.getBookmarks(eventId);
    bookmarkedSessions.value = sessions;
    bookmarkedSpeakers.value = speakers;
    bookmarkedPartners.value = partners;
    bookmarkedGoodies.value = goodies;
    bookmarkedActivities.value = activities;
  };

  // Adds the sessions of an agent's plan in one call, then reloads the bookmarks, which also carries the sessions
  // the API skipped (workshops) as they are.
  const bulkBookmarkSessions = async (eventId: number, sessionIds: number[]): Promise<BulkBookmarkResult> => {
    const result = await $api.v2.bulkBookmarkSessions(eventId, sessionIds);
    // Only the sessions the API added: the ones already bookmarked or skipped (workshops) did not change.
    for (const sessionId of result.added) {
      trackClick('session', 'bookmark', {entityId: sessionId, eventId});
    }
    await getBookmarks(eventId);
    return result;
  };

  const toggleBookmark = async (
      eventId: number,
      type: EventBookmarkType,
      objectId: number,
      bookmarked: boolean,
  ) => {
    const ids = await $api.v2.setBookmarkStatus(eventId, type, objectId, bookmarked);
    switch (type) {
      case EventBookmarkTypeEnum.Session.value:
        bookmarkedSessions.value = ids as BookmarkedSession[];
        break;
      case EventBookmarkTypeEnum.Speaker.value:
        bookmarkedSpeakers.value = ids as number[];
        break;
      case EventBookmarkTypeEnum.Partner.value:
        bookmarkedPartners.value = ids as number[];
        break;
      case EventBookmarkTypeEnum.Goodies.value:
        bookmarkedGoodies.value = ids as number[];
        break;
      case EventBookmarkTypeEnum.Activities.value:
        bookmarkedActivities.value = ids as number[];
        break;
      default:
        console.warn(`Unhandled bookmark type: ${type}`);
    }
  };

  const workshopsCounts = ref<Record<number, WorkshopSeatCount>>({});
  // Tracks which event the loaded counts belong to. The map is keyed by (globally unique)
  // session id, so a stale map from a previous event never matches the new event's sessions —
  // they'd read as zero occupied seats (full availability, open waitlist). Scope the load-once
  // guard to the event so switching events always refetches that event's counts.
  const workshopsCountsEventId = ref<number | null>(null);
  // `force` exists for reconciliation. The load-once guard below is right for navigation — the
  // agenda calls this on every route change — but it also means a caller that genuinely needs
  // fresh numbers silently gets nothing back. That matters after an SSE gap: while the stream
  // was down, `update-counts` messages were missed, so the local map is stale by an unknown
  // amount and re-reading the server is the only way to recover. Reconnect handlers must pass
  // force: true or they are no-ops.
  const loadWorkshopsCounts = async (eventId: number, { force = false } = {}) => {
    if (!force && workshopsCountsEventId.value === eventId) return;
    if (!hasEventTicket(eventId)) return;
    workshopsCounts.value = await $api.v2.getWorkshopsCounts(eventId);
    workshopsCountsEventId.value = eventId;
  }

  const setWorkshopsCounts = (counts: Record<number, WorkshopSeatCount>) => {
    workshopsCounts.value = counts;
  }

  // Merge the new seats count into the existing entry so we don't clobber the
  // `{ seats, waitlist_full }` shape the rest of the app reads. `waitlistFull` is
  // only carried by the authoritative server broadcast; optimistic local updates
  // omit it and keep whatever we already had.
  //
  // Writes the one changed key in place rather than rebuilding the map. `ref({})` is deeply
  // reactive, so assigning a key — including a new one — is tracked; reassigning the whole
  // object copied every entry and invalidated every watcher on it, which during a burst is
  // once per incoming message across an agenda holding dozens of workshops.
  const updateWorkshopCount = (sessionId: number, seats: number, waitlistFull?: boolean) => {
    const existing = workshopsCounts.value[sessionId];
    workshopsCounts.value[sessionId] = {
      waitlist_full: waitlistFull ?? existing?.waitlist_full ?? false,
      seats: Math.max(seats, 0),
    };
  }

  // --- SSE count buffering ---------------------------------------------------------------
  // Every workshop write publishes an `update-counts` message to every connected client, so a
  // registration burst arrives here as a stream, not as occasional events. Applying each one
  // the moment it lands means a re-render per message; the agenda is the screen most attendees
  // are sitting on while that happens.
  //
  // Only the LATEST update per session matters — each payload is a full snapshot of that
  // workshop (count + waitlist_full), not a delta — so intermediate messages for the same
  // session can be dropped outright rather than queued.
  // The coalescing itself lives in utils/workshopCountBuffer.ts, free of Vue and Nuxt imports
  // so it can be exercised on its own. Optimistic local updates and server reconciliation
  // bypass it via discard(): the first is the user's own tap and has to feel instant, the
  // second is authoritative and must not be overwritten by an older buffered snapshot.
  const countBuffer = createWorkshopCountBuffer(updateWorkshopCount, 250);

  // Pull authoritative seat/waitlist state back from the server, discarding whatever the local
  // map drifted to. Safe to call on reconnect, on resume, or after an error.
  const reconcileWorkshopsCounts = async (eventId: number) => {
    // Anything still buffered describes a moment before this fetch, so applying it afterwards
    // would overwrite authoritative numbers with older ones.
    countBuffer.discard();
    await loadWorkshopsCounts(eventId, { force: true });
  }

  //region Events List

  const events : Ref<EventBasic[]> = ref([]);

  const loadEventsList = async () => {
    try {
      events.value = await $api.v2.getEvents();
    } catch(error: any) {
      throw createDataLoadError(error, 'Failed to load events');
    }
  }

  //endregion

  //region Current Event

  const eventData : Ref<EventData|null> = ref(null);
  // Set when loadEvent fails with nothing cached to fall back on. Pages that block on the
  // event data wait on `dataLoaded || eventLoadError` so they surface this (a 503 "You're
  // offline" from createDataLoadError) instead of hanging on `until(dataLoaded)` forever.
  const eventLoadError : Ref<any|null> = ref(null);
  // When the current event's payload last arrived. The payload is served with max-age=60, so asking again inside that
  // minute can only return the same bytes: app.vue uses this to not refetch (and swap the whole 300+ session object
  // under the page that just appeared) on every move between two pages of the same event.
  const eventLoadedAt = ref(0);
  // Whose bookmarks loadEvent() last fetched. Signing out keeps the store, so without this a different user signing in
  // inside the minute would be served the previous user's bookmarks as fresh.
  const eventLoadedForUserId = ref<number | undefined>(undefined);
  const isEventFresh = (eventId: number, maxAgeMs: number) =>
    eventData.value?.id === eventId && dataLoaded.value && eventLoadedForUserId.value === currentUserId.value
      && Date.now() - eventLoadedAt.value < maxAgeMs;
  const eventContactDetails : Ref<EventUserContactDetails|null> = ref(null);
  const isPaidDay = computed(() => eventData.value?.event_type === wadConstants.EVENT_TYPE.PAID_LIVE_DAY);
  const isCongress = computed(() => eventData.value?.event_type === wadConstants.EVENT_TYPE.WORLD_CONGRESS);
  // Attendees, partners, goodies and booth activities open together with the agenda (TAL-11054): before that few
  // people are in the app, and the first visitor would meet an empty room. The API refuses them too in Program Preview.
  const communityAvailable = computed(() => !eventData.value?.program_preview);
  // The API refuses bookmarks and workshop (de)registrations once the event is over (TAL-10913).
  // Hide the controls instead of letting every past attendee run into that error. Decided once
  // per event load in loadEventPayload(), which every page runs first, against the app clock at
  // that moment (so the time override is honoured). Not a live computed: an event ending while
  // the page is open is not a case worth a ticking dependency.
  const eventHasEnded = ref(false);
  const isOnboarded = computed(() => !!eventContactDetails.value?.has_visited_homepage);
  const selectedEventDay = ref(0);
  const eventSpeakers: Ref<EventSpeaker[]> = ref([]);
  const {currentTimestamp} = useCurrentTime();
  const fuse = new Fuse<EventSession>([], {
    keys: ['title', 'speakers.full_name', 'speakers.tagline', 'speakers.company_name', 'track.name', 'tags', 'search_tags', 'stage_search_name'],
    includeScore: true,
    threshold: 0.3,
    distance: 150,
    // Without it a word past about 50 characters of a title is dropped ("kubernetes": 6 of 13 sessions found).
    ignoreLocation: true,
  })

  /**
   * The shared, edge-cached half of an event load: the agenda payload and nothing else.
   *
   * Split out for the SSE 'reload-events' refresh, which fires for every connected
   * attendee at the same moment. The two user-specific requests loadEvent() makes after
   * this one are authenticated and therefore uncacheable, so including them turned one
   * broadcast into three requests per phone against the origin. Neither needs refreshing
   * here: bookmarks and workshop counts have their own signals ('reload-bookmarks' and
   * 'update-counts'), and a change to the event payload says nothing about either.
   */
  const loadEventPayload = async (eventId: number) => {
    const isDifferentEvent = eventData.value?.id !== eventId;
    if (isDifferentEvent) {
      dataLoaded.value = false;
    }
    eventLoadError.value = null;
    try {
      eventData.value = await $api.v2.getEvent(eventId);
      eventHasEnded.value = !!eventData.value?.ends_at && dayjs(eventData.value.ends_at).valueOf() < currentTimestamp.value;
      // Only default the selected day to "today" when first opening (or switching) an event.
      // loadEvent is also re-run whenever the user moves between pages of the same event, and by
      // the SSE 'reload-events' refresh; resetting the day there would clobber the user's current
      // selection (leaving the agenda, coming back, and finding the day chip flicked back to today).
      if (isDifferentEvent) {
        const today = eventDays.value.filter(day => day.isToday);
        selectedEventDay.value = today.length > 0 && today[0] ? today[0].value : 0;
      }
      eventSessionsStatusTime.value = currentTimestamp.value;
      // The cached event response is enough to render the page, so mark it loaded as soon
      // as getEvent() succeeds. Bookmarks aren't cached and workshop counts may be stale,
      // so they're refreshed independently below and must not block the page when offline.
      dataLoaded.value = true;
      eventLoadedAt.value = Date.now();
    } catch(error: any) {
      console.error(error);
      const err = createDataLoadError(error, 'Failed to load event data');
      // Only surface to blocked pages when we have nothing cached to render — a failed
      // background refetch over stale-but-present data should keep showing that data.
      if (!dataLoaded.value) {
        eventLoadError.value = err;
      }
      throw err;
    }
  }

  const loadEvent = async (eventId: number) => {
    eventLoadedForUserId.value = currentUserId.value;
    await loadEventPayload(eventId);

    // User-specific extras: keep whatever we already have (default empty state on a fresh
    // load) when these fail rather than failing the whole event page. Report to Bugsnag so
    // genuine failures surface — its onError already drops offline errors, which is exactly
    // the expected case here. Both getBookmarks and loadWorkshopsCounts no-op on their own
    // when the session shows no ticket for this event, so no ticket check is needed here.
    try {
      await getBookmarks(eventId);
    } catch(error: any) {
      $bugsnag?.notify(error);
    }
    try {
      await loadWorkshopsCounts(eventId);
    } catch(error: any) {
      $bugsnag?.notify(error);
    }
  }

  // Contact details are per event (per ticket), so remember which event the loaded ones belong to.
  const eventContactDetailsEventId = ref<number | null>(null);

  const loadEventContactDetails = async(eventId: number) => {
    try {
      eventContactDetails.value = await $api.v2.getContactDetails(eventId);
      eventContactDetailsEventId.value = eventId;
    } catch(error: any) {
      throw createDataLoadError(error, 'Failed to load contact details');
    }
  }

  // app.vue only loads the contact details while onboarding is still pending, so an onboarded user has
  // none in the store. The topics behind "Recommended" live on them: fetch once, the first time the
  // filter is used, rather than adding a request to every agenda visit.
  const ensureEventContactDetails = async (eventId: number) => {
    if (eventContactDetails.value && eventContactDetailsEventId.value === eventId) {
      return;
    }
    try {
      await loadEventContactDetails(eventId);
    } catch(error: any) {
      // Recommended then runs on bookmarks alone. Offline is the expected case, which Bugsnag's onError drops.
      $bugsnag?.notify(error);
    }
  }

  const onlyBookmarkedSessions = ref<boolean>(false);

  const eventDays = computed(() => {
    if(!eventData.value) {
      return [];
    }
    const tz = eventData.value.timezone ?? 'UTC';
    const start = dayJsTz(eventData.value.starts_at, tz).startOf('day');
    const end = dayJsTz(eventData.value.ends_at, tz).startOf('day');

    const dates: Array<{value: number, label: string, longLabel: string, isToday: boolean, timestamp: number}> = []
    let current = start
    const today = dayJsTz(currentTimestamp.value, tz);
    let dayIndex = 0;
    while (current.isSameOrBefore(end, 'day')) {
      dates.push({
        value: dayIndex,
        label: current.format('ddd Do'),
        longLabel: current.format("dddd - MMMM, Do"),
        isToday: current.isSame(today, 'day'),
        timestamp: current.valueOf()
      })
      current = current.add(1, 'day')
      dayIndex += 1;
    }

    return dates
  })

  const eventSessionsSearch = ref<string|null>(null);
  const eventSessionFilterTracks = ref<number[]>([]);
  const eventSessionFilterStages = ref<number[]>([]);
  const eventSessionFilterSessionTypes = ref<number[]>([]);
  // Session types the Type filter offers. A type can exist in the agenda without being offered as
  // a filter (TAL-10908: Masterclass is hidden for WWC26). Matched by name because the type rows
  // are re-created per event by the Sessionize import and carry no other stable marker.
  const HIDDEN_SESSION_TYPE_FILTER_NAMES = ['masterclass'];
  const eventSessionTypeFilterOptions = computed<EventSessionType[]>(() =>
    (eventData.value?.session_types ?? []).filter(type => !HIDDEN_SESSION_TYPE_FILTER_NAMES.includes(type.name.trim().toLowerCase()))
  );
  const eventSessionFilterFeatured = ref<boolean>(false);
  const eventSessionFilterRecommended = ref<boolean>(false);
  // Start-of-session window as minutes since midnight in the event's timezone. null = no time filter,
  // so a slider left at its full range never counts as an active filter.
  const eventSessionFilterTimeRange = ref<[number, number] | null>(null);

  // Minutes since midnight of every session start, computed once per payload: the filter below runs
  // for every session on every change, and a timezone-aware dayjs per call is the slow part of it.
  const sessionStartMinutes = computed(() => {
    const tz = eventData.value?.timezone ?? 'UTC';
    const minutes = new Map<number, number>();
    (eventData.value?.sessions ?? []).forEach((session) => {
      if (session.starts_at_timestamp) {
        const start = dayJsTz(session.starts_at_timestamp, tz);
        minutes.set(session.id, start.hour() * 60 + start.minute());
      }
    });
    return minutes;
  });

  const TIME_STEP_MINUTES = 30;
  // The slider spans the earliest to the latest session start, rounded outwards to the step.
  const eventSessionTimeBounds = computed<[number, number]>(() => {
    const values = [...sessionStartMinutes.value.values()];
    if (!values.length) {
      return [0, 24 * 60];
    }
    return [
      Math.floor(Math.min(...values) / TIME_STEP_MINUTES) * TIME_STEP_MINUTES,
      Math.ceil(Math.max(...values) / TIME_STEP_MINUTES) * TIME_STEP_MINUTES,
    ];
  });

  const setEventSessionTimeRange = (range: [number, number]) => {
    const [min, max] = eventSessionTimeBounds.value;
    eventSessionFilterTimeRange.value = range[0] <= min && range[1] >= max ? null : range;
  };

  const recommendedSessionIdSet = computed(() => recommendedSessionIds(
    eventData.value?.sessions ?? [],
    new Set(bookmarkedSessions.value.map(b => b.session_id)),
    // Only this event's topics: the store can still hold another event's details after switching events.
    (eventContactDetailsEventId.value === eventData.value?.id ? eventContactDetails.value?.interests_topics ?? [] : []).map(topic => topic.name),
  ));

  const sessionsHaveFilters = computed(() => {
    return onlyBookmarkedSessions.value || eventSessionFilterTracks.value.length > 0 || eventSessionFilterStages.value.length > 0 || eventSessionFilterSessionTypes.value.length > 0
      || eventSessionFilterFeatured.value || eventSessionFilterRecommended.value || eventSessionFilterTimeRange.value !== null;
  })

  // Filters the sheet and chips control. Excludes the Schedule / My Agenda scope (onlyBookmarkedSessions),
  // which is a view switch and not something "Clear" should undo.
  const activeSessionFilterCount = computed(() =>
    eventSessionFilterTracks.value.length + eventSessionFilterStages.value.length + eventSessionFilterSessionTypes.value.length
    + (eventSessionFilterFeatured.value ? 1 : 0) + (eventSessionFilterRecommended.value ? 1 : 0) + (eventSessionFilterTimeRange.value ? 1 : 0)
  );

  const resetSessionFilters = () => {
    eventSessionFilterTracks.value = [];
    eventSessionFilterStages.value = [];
    eventSessionFilterSessionTypes.value = [];
    eventSessionFilterFeatured.value = false;
    eventSessionFilterRecommended.value = false;
    eventSessionFilterTimeRange.value = null;
  };

  const sessionIsFilteredOut = (session: EventSession): boolean => {
    switch (true) {
      case onlyBookmarkedSessions.value && !bookmarkedSessions.value.some(b => b.session_id === session.id):
        return true;
      case eventSessionFilterFeatured.value && !session.featured:
        return true;
      case eventSessionFilterRecommended.value && !recommendedSessionIdSet.value.has(session.id):
        return true;
      case eventSessionFilterTimeRange.value !== null && !isStartWithinRange(session.id, eventSessionFilterTimeRange.value):
        return true;
      case eventSessionFilterTracks.value.length > 0 && (!session.track?.id || !eventSessionFilterTracks.value.includes(session.track?.id)):
        return true;
      case eventSessionFilterSessionTypes.value.length > 0 && (!session.session_type || !eventSessionFilterSessionTypes.value.includes(session.session_type)):
        return true;
      case eventSessionFilterStages.value.length > 0 && (!session.stage?.id || !eventSessionFilterStages.value.includes(session.stage?.id)):
        return true;
      default:
        return false;
    }
  }
  const isStartWithinRange = (sessionId: number, range: [number, number]): boolean => {
    const minutes = sessionStartMinutes.value.get(sessionId);
    return minutes !== undefined && minutes >= range[0] && minutes <= range[1];
  };

  const eventSessions = computed(() => {
    if (eventSessionsSearch.value) {
      if(sessionsHaveFilters.value) {
        fuse.setCollection((eventData.value?.sessions || []).filter(session => !sessionIsFilteredOut(session)));
      } else {
        fuse.setCollection(eventData.value?.sessions || [] );
      }
      // In time order, not by score: the agenda lists the results under day and time headers, and in score order
      // every result could start a new header (Thursday, Friday, Thursday, ...).
      return fuse.search(eventSessionsSearch.value).map((r) => r.item).sort((a, b) => a.starts_at_timestamp - b.starts_at_timestamp);
    } else {
      if(sessionsHaveFilters.value){
        return (eventData.value?.sessions || []).filter(session => !sessionIsFilteredOut(session));
      } else {
        return eventData.value?.sessions || [];
      }
    }
  })

  // A search, a filter or My Agenda narrows the agenda to matches, which it then lists from every day, grouped by
  // day. Only the plain schedule is listed one day at a time.
  const eventSessionsNarrowed = computed(() => !!eventSessionsSearch.value || sessionsHaveFilters.value);

  // The days (event_day) that have at least one session under the current search, filters and scope: the day strip
  // can't pick the others.
  const eventSessionDays = computed(() => new Set((eventSessions.value || []).flatMap(session => session.event_day === null ? [] : [session.event_day])));

  const eventSessionsStatusTime = ref<number>(0);

  const eventSessionsPast = computed(() => {
    return (eventSessions.value || []).filter(session => session.ends_at_timestamp < eventSessionsStatusTime.value);
  })

  const eventSessionsLive = computed(() => {
    return (eventSessions.value || []).filter(session => session.starts_at_timestamp <= eventSessionsStatusTime.value && session.ends_at_timestamp >= eventSessionsStatusTime.value);
  })

  const eventSessionsUpcoming = computed(() => {
    return (eventSessions.value || []).filter(session => session.starts_at_timestamp > eventSessionsStatusTime.value);
  })


  //endregion

  //region Speakers

  const eventSpeakersSearch = ref<string|null>(null);
  const eventPartnersSearch = ref<string|null>(null);
  const eventGoodiesSearch = ref<string|null>(null);
  const eventActivitiesSearch = ref<string|null>(null);

  //endregion


  //region Tickets

  const ticketValidation: Ref<EventTicketValidation|null> = ref(null);

  const loadTicketValidation = async (ticketToken: string) => {
    ticketValidation.value = <EventTicketValidation>{
      ... await $api.v2.checkToken(ticketToken),
      ticket_token: ticketToken
    }
  }

  const clearTicketValidation = () => {
    ticketValidation.value = null;
  }

  //endregion

  return {
    events,
    loadEventsList,

    //Agenda
    eventData,
    isPaidDay,
    isCongress,
    communityAvailable,
    eventHasEnded,
    isOnboarded,
    eventSessions,
    eventSessionsPast,
    eventSessionsLive,
    eventSessionsUpcoming,
    eventSessionsStatusTime,
    eventSessionDays,
    eventSessionsNarrowed,
    eventDays,
    selectedEventDay,
    eventSessionsSearch,
    eventSessionFilterTracks,
    eventSessionFilterSessionTypes,
    eventSessionTypeFilterOptions,
    eventSessionFilterStages,
    eventSessionFilterFeatured,
    eventSessionFilterRecommended,
    eventSessionFilterTimeRange,
    eventSessionTimeBounds,
    setEventSessionTimeRange,
    activeSessionFilterCount,
    resetSessionFilters,
    loadEvent,
    loadEventPayload,
    isEventFresh,
    eventLoadError,
    loadEventContactDetails,
    ensureEventContactDetails,
    eventSpeakers,
    eventContactDetails,

    //Speakers
    eventSpeakersSearch,
    eventPartnersSearch,
    eventGoodiesSearch,
    eventActivitiesSearch,

    //Tickets
    ticketValidation,
    loadTicketValidation,
    clearTicketValidation,

    //Bookmarks
    onlyBookmarkedSessions,
    bookmarkedSessions,
    bookmarkedSpeakers,
    bookmarkedPartners,
    bookmarkedGoodies,
    bookmarkedActivities,
    getBookmarks,
    toggleBookmark,
    bulkBookmarkSessions,
    workshopsCounts,
    loadWorkshopsCounts,
    reconcileWorkshopsCounts,
    setWorkshopsCounts,
    updateWorkshopCount,
    queueWorkshopCountUpdate: countBuffer.queue,
    discardPendingCountUpdates: countBuffer.discard,

    dataLoaded,
  };

}, {},)
